import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react'
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux'

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core'

import { Button } from 'web-components';
import { useClickAway } from 'web-hooks';
import * as S from './styled';
import { Flex } from 'web-styled';
import { placeCaretAtEnd, trackAnalyticsEvent } from 'web-utils';

// Types
interface ClientNotesProps {
  clientId: number
  disabled?: boolean;
  initialValue?: string;
  onSave?: (value: string) => void;
}

const DEFAULT_PLACEHOLDER = "Click Edit Notes to add additional information about your client";

// Custom hooks
const useNoteEditor = (initialValue: string = '') => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [noteValue, setNoteValue] = useState(initialValue);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const startEditing = useCallback(() => {
    setIsEditing(true);
    setTimeout(() => {
      const textarea = noteRef.current;
      if (textarea) {
        placeCaretAtEnd(textarea);
        textarea.scrollTop = textarea.scrollHeight;
      }
    }, 50);
  }, []);

  const stopEditing = useCallback(() => {
    setIsEditing(false);
  }, [])

  const toggleEditing = useCallback(() => {
    if (isEditing) {
      stopEditing()
    } else {
      startEditing()
      trackAnalyticsEvent('opened_client_notes')
    }
  }, [isEditing, startEditing, stopEditing])

  return {
    isSaving,
    isEditing,
    noteValue,
    setNoteValue,
    noteRef,
    setIsSaving,
    startEditing,
    stopEditing,
    toggleEditing,
  };
};

// Sub-components
const NoteHeader: React.FC<{
  isEditing: boolean;
  isSaving: boolean;
  onToggleEdit: () => void;
  onSave?: () => void;
  onCancel?: () => void;
}> = ({ isEditing, isSaving, onToggleEdit, onSave, onCancel }) => (
  <div className="card-header">
    <h5 className="card-title">Notes</h5>
    <div className="button-group">
      {isEditing ? (
        <Flex spacing={5}>
          {onSave && (
            <Button
              ghost
              isLoading={isSaving}
              isDisabled={isSaving}
              appearance="primary"
              spacing="compact"
              onClick={onSave}
            >
              Save
            </Button>
          )}
          {onCancel && (
            <Button
              ghost
              spacing="compact"
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
        </Flex>
      ) : (
        <Button
          ghost
          appearance="primary"
          spacing="compact"
          onClick={onToggleEdit}
        >
          Edit Notes
        </Button>
      )}
    </div>
  </div>
);

const NoteEditor: React.FC<{
  noteRef: React.RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}> = ({ noteRef, value, onChange, placeholder }) => (
  <S.TextBox
    ref={noteRef}
    autoSize={false}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    autoFocus
  />
);

const NoteDisplay: React.FC<{
  value: string;
  placeholder: string;
}> = ({ value, placeholder }) => (
  <S.NoteDisplay>
    {value ? (
      <div className="note-content">
        {value.split('\n').map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {index < value.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </div>
    ) : (
      <div className="empty-note">{placeholder}</div>
    )}
  </S.NoteDisplay>
);

// Main component
const ClientNotes: React.FC<ClientNotesProps> = ({
  clientId,
  disabled = false,
  initialValue = '',
}) => {
  const mapState = useCallback(state => ({
    notes: coreSelectors.getResourceMappedList(
      'clientNotes',
      String(clientId)
    )(state),
  }), [clientId]);
  const dispatch = useDispatch();
  const { notes } = useSelector(mapState, shallowEqual);
  const noteId = notes[0]?.id ? notes[0].id : null

  const {
    isSaving,
    isEditing,
    noteValue,
    setIsSaving,
    setNoteValue,
    noteRef,
    toggleEditing,
    stopEditing,
  } = useNoteEditor(initialValue);

  const [tempValue, setTempValue] = useState(noteValue);

  useEffect(() => {
    dispatch(
      coreActions.requestFetchClientNotes({
        clientId,
        callbacks: {
          onSuccess: (response) => {
            if (response[0]) {
              setNoteValue(response[0].description)
            }
          }
        }
      }),
    )
  }, [clientId]);

  useEffect(() => {
    if (isEditing) {
      setTempValue(noteValue);
    }
  }, [isEditing, noteValue]);

  const handleSave = useCallback(() => {
    setNoteValue(tempValue)
    setIsSaving(true)
    const callbacks = {
      setSubmitting: () => {
        stopEditing()
        setIsSaving(false)
      }
    }

    if (noteId) {
      if (tempValue) {
        dispatch(
          coreActions.requestUpdateClientNotes({
            id: noteId,
            description: tempValue,
            callbacks,
          })
        )
      } else {
        dispatch(
          coreActions.requestDeleteClientNotes({
            id: noteId,
            callbacks,
          })
        )
      }
    } else {
      dispatch(
        coreActions.requestCreateClientNotes({
          clientId,
          description: tempValue,
          callbacks,
        })
      )
    }
  }, [tempValue, clientId, noteId, stopEditing, setNoteValue]);

  const handleCancel = useCallback(() => {
    setTempValue(noteValue);
    stopEditing();
  }, [noteValue, stopEditing]);

  const handleClickAway = useCallback(() => {
    if (isEditing) {
      handleCancel();
    }
  }, [isEditing, handleSave]);

  const ref = useClickAway(handleClickAway, { disabled });

  return (
    <S.Card ref={ref}>
      <NoteHeader
        isSaving={isSaving}
        isEditing={isEditing}
        onToggleEdit={toggleEditing}
        onSave={handleSave}
        onCancel={handleCancel}
      />

      {isEditing ? (
        <NoteEditor
          noteRef={noteRef}
          value={tempValue}
          onChange={setTempValue}
          placeholder={DEFAULT_PLACEHOLDER}
        />
      ) : (
        <NoteDisplay
          value={noteValue}
          placeholder={DEFAULT_PLACEHOLDER}
        />
      )}
    </S.Card>
  );
};

export default ClientNotes;
